import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { auth } from '../firebase/firebaseConfig';
import { signOut } from 'firebase/auth';
import { useAuth } from '../context/AuthContext';
import { useLatestVersion } from '../data/changelogData';
import { useNotifications } from '../hooks/useNotifications';
import './AdminLayout.css';

function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const version = useLatestVersion();
  const { unreadCount } = useNotifications();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/admin/login');
    } catch (error) {
      console.error("Failed to log out", error);
    }
  };

  // Helper to check permissions
  const hasAccess = (allowedRoles) => {
    if (!userProfile?.role) return false;
    if (userProfile.role === 'superadmin') return true; // Superadmin has all access
    return allowedRoles.includes(userProfile.role);
  };

  const closeSidebarMobile = () => {
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="admin-wrapper">
      
      {/* Mobile Overlay */}
      <div 
        className={`admin-sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      ></div>

      {/* Sidebar Panel */}
      <aside className={`admin-sidebar ${sidebarOpen ? '' : 'closed'}`}>
        <div className="admin-sidebar-header">
          <i className="fa-solid fa-shield-halved"></i>
          <div className="sidebar-logo-text">
            <h2>DevTracker</h2>
            <span>Admin Nexus</span>
          </div>
          <button className="admin-close-btn" onClick={() => setSidebarOpen(false)}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        <nav className="admin-nav">
          <div className="admin-nav-group">
            <p>CORE_DOMAINS</p>
            <NavLink to="/admin/dashboard" title="Dashboard" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
              <i className="fa-solid fa-chart-pie"></i> <span>Dashboard</span>
            </NavLink>
          </div>

          <div className="admin-nav-group">
            <p>CITIZEN_MANAGEMENT</p>
            {hasAccess(['moderator']) && (
              <>
                <NavLink to="/admin/users" title="Users" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
                  <i className="fa-solid fa-users"></i> <span>User Registry</span>
                </NavLink>
                <NavLink to="/admin/profiles" title="Profiles" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
                  <i className="fa-regular fa-id-card"></i> <span>Profile Metadata</span>
                </NavLink>
              </>
            )}
            {userProfile?.role === 'superadmin' && (
              <>
                <NavLink to="/admin/admins" title="Administrators" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
                  <i className="fa-solid fa-user-shield"></i> <span>Admin Hierarchy</span>
                </NavLink>
                <NavLink to="/admin/statistics" title="API Statistics" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
                  <i className="fa-solid fa-server"></i> <span>Global Telemetry</span>
                </NavLink>
              </>
            )}
          </div>

          <div className="admin-nav-group">
            <p>TRUST_GOVERNANCE</p>
            {hasAccess(['moderator']) && (
              <>
                <NavLink to="/admin/security-review" title="Security Review" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
                  <i className="fa-solid fa-file-shield"></i> <span>Security Audit</span>
                </NavLink>
                <NavLink to="/admin/dna-protocol" title="DNA Protocol" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
                  <i className="fa-solid fa-dna"></i> <span>DNA Matrix SOP</span>
                </NavLink>
              </>
            )}
            {hasAccess(['support']) && (
              <NavLink to="/admin/identity-search" title="Target Identity" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
                <i className="fa-solid fa-id-card-clip"></i> <span>Identity Decrypt (SUID)</span>
              </NavLink>
            )}
          </div>

          <div className="admin-nav-group">
            <p>OPERATION_CENTRAL</p>
            {hasAccess(['support']) && (
              <NavLink to="/admin/messages" title="Contact Messages" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
                <i className="fa-solid fa-envelope"></i> <span>Communications</span>
              </NavLink>
            )}
            {userProfile?.role === 'superadmin' && (
              <NavLink to="/admin/notifications" title="Broadcast Notifications" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
                <i className="fa-solid fa-bullhorn"></i> <span>System Broadcast</span>
              </NavLink>
            )}
            {hasAccess(['moderator', 'support']) && (
              <NavLink to="/admin/reports" title="User Reports & Appeals" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
                <i className="fa-solid fa-flag"></i> <span>Triage & Appeals</span>
              </NavLink>
            )}
            {userProfile?.role === 'superadmin' && (
              <>
                <NavLink to="/admin/changelog" title="System Updates" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
                  <i className="fa-solid fa-clock-rotate-left"></i> <span>System Changelog</span>
                </NavLink>
                <NavLink to="/admin/logs" title="System Logs" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
                  <i className="fa-solid fa-terminal"></i> <span>Audit Streams</span>
                </NavLink>
                <NavLink to="/admin/errors" title="Error Monitor" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
                  <i className="fa-solid fa-bug"></i> <span>Debug Console</span>
                </NavLink>
              </>
            )}
            <NavLink to="/admin/docs" title="Internal Docs" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
              <i className="fa-solid fa-book"></i> <span>Nexus SOP</span>
            </NavLink>
            {userProfile?.role === 'superadmin' && (
              <NavLink to="/admin/ai-intel" title="DevTracker AI Hub" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
                <i className="fa-solid fa-brain"></i> <span>System Intel Hub</span>
              </NavLink>
            )}
            {hasAccess(['support']) && (
              <NavLink to="/admin/consultations" title="AI Consultations" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
                <i className="fa-solid fa-headset"></i> <span>System Support</span>
              </NavLink>
            )}
            {userProfile?.role === 'superadmin' && (
              <NavLink to="/admin/settings" title="Settings" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
                <i className="fa-solid fa-gear"></i> <span>System Variables</span>
              </NavLink>
            )}
          </div>

          <div className="admin-nav-group">
            <p>NAVIGATION</p>
            <NavLink to="/admin/sitemap" title="System Sitemap" onClick={closeSidebarMobile} className={({isActive}) => isActive ? "active" : ""}>
              <i className="fa-solid fa-compass"></i> <span>System Sitemap</span>
            </NavLink>
          </div>
        </nav>

        <div className="admin-sidebar-footer">
          <button onClick={handleLogout} className="admin-logout-btn" title="Logout">
            <i className="fa-solid fa-arrow-right-from-bracket"></i> <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Right Side Content */}
      <div className={`admin-main-container ${sidebarOpen ? '' : 'sidebar-closed'}`}>
        
        {/* Topbar */}
        <header className="admin-topbar">
          <button className="admin-hamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <i className="fa-solid fa-bars"></i>
          </button>
          
            <div className="admin-topbar-right">
              <button className="admin-notif-bell" onClick={() => window.dispatchEvent(new CustomEvent('toggle-notif-panel'))}>
                <i className="fa-solid fa-bell"></i>
                {unreadCount > 0 && <span className="admin-unread-dot">{unreadCount}</span>}
              </button>
              
              <a href="/" target="_blank" rel="noreferrer" className="admin-view-site-btn">
                View Live Site <i className="fa-solid fa-arrow-up-right-from-square" style={{marginLeft: '5px'}}></i>
              </a>
              
              <div className="admin-avatar-container" onClick={() => setDropdownOpen(!dropdownOpen)}>
                <div className="admin-avatar">
                  <i className="fa-solid fa-user-tie"></i>
                </div>
              
              <div className={`admin-dropdown ${dropdownOpen ? 'open' : ''}`}>
                <div className="admin-dropdown-header">
                  <strong style={{fontSize: '1.05rem', color: '#fff'}}>{userProfile?.name || "Administrator"}</strong>
                  <div style={{color: '#8b949e', fontSize: '0.8rem', marginTop: '3px', fontFamily: 'monospace'}}>{userProfile?.adminId || "AID-DVT-PENDING"}</div>
                  <div style={{marginTop: '8px', display: 'inline-flex', alignItems: 'center', background: 'rgba(255,255,255,0.05)', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem'}}>
                    {userProfile?.role === 'superadmin' && <><span style={{color: '#ff375f', marginRight: '5px'}}>🔴</span> Super Admin</>}
                    {userProfile?.role === 'moderator' && <><span style={{color: '#ffa116', marginRight: '5px'}}>🟠</span> Moderator</>}
                    {userProfile?.role === 'support' && <><span style={{color: '#00ffae', marginRight: '5px'}}>🟢</span> Support</>}
                    {!userProfile?.role && <><span style={{color: '#8b949e', marginRight: '5px'}}>⚪</span> Pending</>}
                  </div>
                </div>
                <NavLink to="/admin/settings" onClick={() => setDropdownOpen(false)} className="admin-dropdown-item">
                  <i className="fa-solid fa-gear"></i> Settings
                </NavLink>
                <button onClick={handleLogout} className="admin-dropdown-item text-danger">
                  <i className="fa-solid fa-arrow-right-from-bracket"></i> Logout
                </button>
              </div>
            </div>
            
          </div>
        </header>

        {/* Content Outlet where pages render */}
        <main className="admin-content-area">
          <Outlet />
        </main>
        
        {/* Admin Footer */}
        <footer className="admin-footer">
          <p>&copy; 2026 DevTracker Admin Panel</p>
          <p>System Time: {new Date().toUTCString()}</p>
          <p>Version {version}</p>
        </footer>
      </div>

    </div>
  );
}

export default AdminLayout;
